<template>
  <div class="rule-builder mt-3">
    <div class="header d-flex justify-content-between align-items-start mb-3">
      <div>
        <h6 class="rb-title mb-0">CTC Amount Rule Builder</h6>
        <div class="rb-subtitle">Define conditional amounts based on employee attributes.</div>
      </div>
      <button v-if="value && value.field" type="button" class="btn rb-btn-reset" @click="resetTree">Reset Rules</button>
    </div>

    <!-- Level 1 Field Selection -->
    <div v-if="!value || !value.field" class="initial-setup">
      <p class="mb-3">Define conditional amounts based on employee attributes.</p>
      <select
        class="form-control d-inline-block"
        @change="initializeRootNodes($event.target.value)"
      >
        <option value="">-- Select Attribute --</option>
        <option v-for="field in fields" :key="field.id" :value="field.id">
          {{ field.label }}
        </option>
      </select>
    </div>

    <!-- Root Tree Rendering -->
    <div v-else>
      <div class="rb-level-row">
        <span class="rb-level-badge">Level 1</span>
        <span class="rb-level-field">{{ getFieldLabel(value.field) }}</span>
      </div>

      <div class="tree-container">
        <div v-for="node in value.nodes" :key="node.value" class="root-node-wrapper mb-3">
          <!-- Root Header -->
          <div
            class="node-header"
            :class="{ 'is-collapsed': node.collapsed }"
            @click="toggleCollapse(node)"
          >
            <i :class="['fas rb-caret', node.collapsed ? 'fa-chevron-right' : 'fa-chevron-down']"></i>
            <span class="rb-node-label">{{ getOptionLabel(value.field, node.value) }}</span>
            <span class="rb-node-amount" v-if="node.amount">৳{{ node.amount }}</span>
          </div>

          <!-- Root Content -->
          <div v-show="!node.collapsed" class="node-content">
            <div class="d-flex align-items-center">
              <div class="input-group input-group-sm rb-amount-group">
                <div class="input-group-prepend"><span class="input-group-text">৳</span></div>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="node.amount"
                  placeholder="Base amount"
                >
              </div>
              <button
                v-if="!node.childField"
                type="button"
                class="btn rb-btn-add ml-2"
                @click="handleAddCondition(node, 2)"
              >
                Add Condition
              </button>
              <button
                v-else
                type="button"
                class="btn rb-btn-remove ml-2"
                @click="removeCondition(node)"
              >
                Remove {{ getFieldLabel(node.childField) }}
              </button>
            </div>

            <!-- Child Field Selector -->
            <div v-if="node.showFieldSelector" class="rb-selector-box mt-3">
              <label class="rb-selector-label">Select Sub-Condition (Level 2)</label>
              <div class="d-flex align-items-center">
                <select
                  class="form-control form-control-sm mr-2"
                  v-model="node.pendingChildField"
                >
                  <option value="">-- Select Attribute --</option>
                  <option
                    v-for="field in getAvailableFields([value.field])"
                    :key="field.id"
                    :value="field.id"
                  >
                    {{ field.label }}
                  </option>
                </select>
                <button type="button" class="btn rb-btn-add" @click="confirmAddCondition(node)">Add</button>
                <button type="button" class="btn rb-btn-cancel ml-1" @click="node.showFieldSelector = false">Cancel</button>
              </div>
            </div>

            <!-- Recursive Children. The level-2 options sit flush under the base
                 amount; only level 3 and below are hung off a dashed rule. -->
            <div v-if="node.childField" class="rb-children">
              <ctc-rule-builder-node
                v-for="child in node.children"
                :key="child.value"
                :node="child"
                :level="2"
                :parent-amount="node.amount"
                :used-fields="[value.field, node.childField]"
                :fields="fields"
                :current-field-id="node.childField"
                @add-condition="(childNode) => handleAddCondition(childNode, 3)"
                @remove-condition="(childNode) => removeCondition(childNode)"
                @confirm-add="(payload) => confirmAddCondition(payload.node)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Warning Modal for Insufficient Custom Fields -->
    <b-modal 
      id="bv-modal-insufficient-fields" 
      size="sm" 
      hide-footer 
      centered
      title="Setup Required"
      header-bg-variant="light"
      header-class="py-2 px-3 border-bottom"
    >
      <div class="text-center p-2">
        <div class="mb-3 text-warning">
          <b-icon icon="exclamation-triangle-fill" font-scale="3"></b-icon>
        </div>
        <h6 class="font-weight-bold mb-2">Additional CTC Attribute Needed</h6>
        <p class="text-muted small mb-3">
          To build a Level {{ pendingTargetLevel }} condition, you need to configure at least {{ pendingTargetLevel }} Custom Fields with <strong>'is CTC'</strong> enabled.
        </p>
        <div class="text-left bg-light p-3 rounded mb-3 border">
          <div class="small font-weight-bold mb-2 text-secondary">Instructions:</div>
          <ol class="small text-muted pl-3 mb-0">
            <li class="mb-1">Go to <strong>Custom Fields</strong> settings</li>
            <li class="mb-1">Create or edit a field</li>
            <li>Enable the <strong>is CTC</strong> option</li>
          </ol>
        </div>
        <b-button variant="dark" size="sm" class="w-100" @click="$bvModal.hide('bv-modal-insufficient-fields')">
          Understood
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'RuleBuilder',
  components: { },
  props: {
    value: {
      type: Object,
      default: () => ({ field: '', nodes: [] })
    },
    fields: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      pendingTargetLevel: 2
    };
  },
  methods: {
    handleAddCondition(node, targetLevel) {
      if (targetLevel === 2 && this.fields.length <= 1) {
        this.pendingTargetLevel = 2;
        this.$bvModal.show('bv-modal-insufficient-fields');
        return;
      }
      if (targetLevel === 3 && this.fields.length <= 2) {
        this.pendingTargetLevel = 3;
        this.$bvModal.show('bv-modal-insufficient-fields');
        return;
      }
      node.showFieldSelector = true;
    },
    getFieldLabel(id) {
      const field = this.fields.find(f => f.id == id);
      return field ? field.label : id;
    },
    getOptionLabel(fieldId, optionValue) {
      const field = this.fields.find(f => f.id == fieldId);
      if (field && field.options) {
        const opt = field.options.find(o => o.value == optionValue);
        return opt ? opt.text : optionValue;
      }
      return optionValue;
    },
    initializeRootNodes(fieldId) {
      if (!fieldId) return;
      const field = this.fields.find(f => f.id == fieldId);
      if (field) {
        const nodes = field.options.map(opt => ({
          value: opt.value,
          amount: null,
          collapsed: false,
          childField: null,
          children: [],
          showFieldSelector: false,
          pendingChildField: ''
        }));
        this.$emit('input', { field: fieldId, nodes });
      }
    },
    resetTree() {
      this.$bvModal.msgBoxConfirm('Please confirm that you want to reset the entire rule builder.', {
        title: 'Please Confirm',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'primary',
        okTitle: 'Yes',
        cancelTitle: 'Cancel',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      }).then(value => {
        if (value) {
          this.$emit('input', { field: '', nodes: [] });
          this.$emit('reset');
        }
      });
    },
    toggleCollapse(node) {
      node.collapsed = !node.collapsed;
      this.$forceUpdate();
    },
    getAvailableFields(usedFields) {
      // Use loose equality or string conversion to ensure IDs match regardless of type (string vs number)
      return this.fields.filter(f => !usedFields.some(usedId => usedId == f.id));
    },
    confirmAddCondition(node) {
      if (!node.pendingChildField) return;
      const fieldData = this.fields.find(f => f.id == node.pendingChildField);
      if (fieldData) {
        node.childField = node.pendingChildField;
        node.children = fieldData.options.map(opt => ({
          value: opt.value,
          amount: null,
          childField: null,
          children: [],
          showFieldSelector: false,
          pendingChildField: ''
        }));
        node.showFieldSelector = false;
        node.pendingChildField = '';
        this.$forceUpdate();
      }
    },
    removeCondition(node) {
      this.$bvModal.msgBoxConfirm('Please confirm that you want to delete this condition level.', {
        title: 'Please Confirm',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'primary',
        okTitle: 'Yes',
        cancelTitle: 'Cancel',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      }).then(value => {
        if (value) {
          node.childField = null;
          node.children = [];
          this.$forceUpdate();
        }
      });
    }
  }
}
</script>

<style scoped>
/* Palette and geometry are the Figma "Create New CTC Item" rule builder: a
   #BDDBCE hairline card at 4px radius, teal #004242/#01524B type, the pale
   #F2FAF5 node header and #CC5057 for anything destructive. */
.rule-builder {
    border: 1px solid #BDDBCE !important;
    border-radius: 4px !important;
    padding: 15px !important;
    background-color: #ffffff !important;
    font-family: 'Poppins', sans-serif;
}

.rb-title {
    font-size: 15px;
    font-weight: 600;
    line-height: 23px;
    letter-spacing: -0.02em;
    color: #004242;
}

.rb-subtitle {
    font-size: 12px;
    font-weight: 500;
    line-height: 23px;
    letter-spacing: -0.02em;
    color: #747474;
}

/* Destructive actions are outlined, never filled: the red edge with the lighter
   #F05B64 label, matching "Reset Rules" and the row delete in the design. */
.rb-btn-reset {
    background-color: #ffffff;
    border: 1px solid #CC5057;
    border-radius: 4px;
    color: #F05B64;
    font-size: 12px;
    font-weight: 500;
    line-height: 19px;
    letter-spacing: -0.04em;
    padding: 5px 10px;
    text-transform: capitalize;
    white-space: nowrap;
}
.rb-btn-reset:hover {
    background-color: #CC5057;
    color: #ffffff;
}

/* Level marker: the dark chip carries the level, the field name sits beside it
   in teal, and a hairline closes the strip off from the tree below. */
.rb-level-row {
    display: flex;
    align-items: center;
    padding-bottom: 8px;
    margin-bottom: 12px;
    border-bottom: 1px solid #BDDBCE;
}

.rb-level-badge {
    background-color: #01524B;
    border-radius: 3px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: -0.02em;
    padding: 4px 12px;
    margin-right: 10px;
}

.rb-level-field {
    font-size: 12px;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: #01524B;
}

/* Header and body are one box split in two: the header keeps the bottom edge
   open so the pair reads as a single outlined card when expanded. */
.node-header {
    display: flex;
    align-items: center;
    min-height: 32px;
    padding: 5px 10px;
    background-color: #F2FAF5;
    border: 1px solid #BDDBCE;
    border-bottom: none;
    border-radius: 4px 4px 0 0;
    cursor: pointer;
}
.node-header.is-collapsed {
    border-bottom: 1px solid #BDDBCE;
    border-radius: 4px;
}

.rb-caret {
    font-size: 11px;
    color: #004242;
    margin-right: 10px;
}

.rb-node-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #01524B;
}

.rb-node-amount {
    margin-left: auto;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #004242;
}

.node-content {
    border: 1px solid #BDDBCE;
    border-top: none;
    border-radius: 0 0 4px 4px;
    padding: 10px 10px 20px;
}

/* The ৳ prefix is a solid teal cap fused to the amount field — one control, so
   the two share an outline rather than each drawing their own. */
.rb-amount-group {
    flex: 1 1 auto;
    min-width: 0;
}

.rb-amount-group .input-group-text {
    background-color: #01524B;
    border: 1px solid #01524B;
    border-radius: 4px 0 0 4px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    padding: 0 9px;
}

.rule-builder .form-control {
    height: 27px;
    border: 1px solid #BDDBCE;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #01524B;
    background-color: #ffffff;
    box-shadow: none;
}

.rb-amount-group .form-control {
    border-left: none;
    border-radius: 0 4px 4px 0;
}

.rule-builder .form-control::placeholder {
    color: #8F8F8F;
    font-size: 11px;
    font-weight: 600;
}

.rule-builder .form-control:focus {
    border-color: #01524B;
    box-shadow: 0 0 0 2px rgba(1, 82, 75, 0.12);
    outline: none;
}

.rb-btn-add {
    background-color: #01524B;
    border: 1px solid #01524B;
    border-radius: 4px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: -0.04em;
    padding: 5px 10px;
    height: 27px;
    text-transform: capitalize;
    white-space: nowrap;
}
.rb-btn-add:hover {
    background-color: #013e36;
    border-color: #013e36;
    color: #ffffff;
}

.rb-btn-remove {
    background-color: #ffffff;
    border: 1px solid #CC5057;
    border-radius: 4px;
    color: #F05B64;
    font-size: 12px;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: -0.04em;
    padding: 5px 10px;
    height: 27px;
    text-transform: capitalize;
    white-space: nowrap;
}
.rb-btn-remove:hover {
    background-color: #CC5057;
    color: #ffffff;
}

.rb-btn-cancel {
    background: none;
    border: none;
    color: #747474;
    font-size: 12px;
    font-weight: 500;
    padding: 5px 8px;
    text-transform: capitalize;
}
.rb-btn-cancel:hover {
    color: #01524B;
}

.rb-selector-box {
    border: 1px solid #BDDBCE;
    border-radius: 4px;
    padding: 12px;
    background-color: #F2FAF5;
}

.rb-selector-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #01524B;
    margin-bottom: 6px;
}

.rb-children {
    margin-top: 12px;
}

.initial-setup {
    border: 1px dashed #BDDBCE !important;
    border-radius: 4px !important;
    padding: 32px 24px !important;
    background-color: #ffffff !important;
    text-align: center !important;
}
.initial-setup p {
    color: #747474 !important;
    font-size: 13px !important;
    font-weight: 500 !important;
}
.initial-setup select.form-control {
    border: 1px solid #004242 !important;
    border-radius: 8px !important;
    height: 36px !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    color: #004242 !important;
    padding: 0 32px 0 16px !important;
    background-color: #ffffff !important;
    display: inline-block !important;
    width: auto !important;
    min-width: 220px !important;
}
.initial-setup select.form-control:focus {
    border-color: #004242 !important;
    box-shadow: 0 0 0 3px rgba(0, 66, 66, 0.12) !important;
    outline: none !important;
}

/* One teal chevron, as on the modal's own selects (Payment Frequency and the
   rest) — the stacked up/down pair reads as a stepper the user can click to
   change the value, which a select isn't. */
.initial-setup select,
.rule-builder select {
    appearance: none !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%2301524B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpath d='M4 6l4 4 4-4'/%3e%3c/svg%3e") !important;
    background-repeat: no-repeat !important;
    background-position: right 10px center !important;
    background-size: 14px 14px !important;
    padding-right: 30px !important;
}

/* The amount field carries no chevron — it is typed, not stepped, so the native
   spinner is suppressed and nothing is drawn in its place. */
.rule-builder input[type="number"]::-webkit-outer-spin-button,
.rule-builder input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none !important;
    margin: 0 !important;
}

.rule-builder input[type="number"] {
    -moz-appearance: textfield !important;
    appearance: textfield !important;
    background-image: none !important;
}
</style>
